import { ClerkProvider, useAuth, useClerk } from '@clerk/clerk-react';
import { LocalizationResource } from '@clerk/types';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';

import i18n from '@repo/i18n';
import { en, it } from '@repo/i18n/clerk';
import { FALLBACK_LOCALE } from '@repo/i18n/supported-locales';
import '@repo/ui/styles.css';

import { router } from '~/router.ts';

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

declare global {
  interface Window {
    _appConfig: AppConfig;
  }
}

export interface AppConfig {
  clerkKey: string;
  graphqlEndpoint: string;
  restEndpoint: string;
  iosUrl: string;
  androidUrl: string;
  version: string;
}

const availableClerkTranslations: Record<string, LocalizationResource> = {
  it,
  en,
};

async function fetchAppConfig(): Promise<AppConfig> {
  if (import.meta.env.DEV) {
    return {
      clerkKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
      graphqlEndpoint: import.meta.env.VITE_GRAPHQL_API_ENDPOINT,
      restEndpoint: import.meta.env.VITE_REST_ENDPOINT,
      iosUrl: import.meta.env.VITE_IOS_URL,
      androidUrl: import.meta.env.VITE_ANDROID_URL,
      version: import.meta.env.VITE_APP_VERSION,
    };
  }

  return fetch('/configs/config.json').then((res) => {
    if (!res.ok) {
      throw new Error();
    }
    return res.json();
  });
}

void (async function bootstrap() {
  const appConfig = await fetchAppConfig();
  window._appConfig = appConfig;

  const InnerApp = () => {
    const clerk = useClerk();
    const { getToken } = useAuth();

    return <RouterProvider router={router} context={{ i18n, clerk, appConfig, getToken }} />;
  };

  const OuterApp = () => {
    const [clerkTranslations, setClerkTranslations] = useState<typeof it>(
      availableClerkTranslations[FALLBACK_LOCALE]
    );

    const [lng, setLng] = useState(i18n.language);

    useEffect(() => {
      i18n.on('languageChanged', (lng) => {
        setLng(lng);
        if (availableClerkTranslations[lng]) {
          setClerkTranslations(availableClerkTranslations[lng]);
        }
      });
    }, []);

    return (
      <ClerkProvider
        publishableKey={appConfig.clerkKey}
        afterSignOutUrl="/"
        signUpUrl={`/${lng}/sign-up`}
        signInUrl={`/${lng}/sign-in`}
        signInFallbackRedirectUrl="/"
        signUpFallbackRedirectUrl="/"
        localization={clerkTranslations}
      >
        <InnerApp />
      </ClerkProvider>
    );
  };

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <OuterApp />
    </StrictMode>
  );
})();
